import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import { PAD_START_ATTR } from '../../input/focusNav';
import { cubeAccent } from '../../render/icons';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { CubePreview } from '../components/CubePreview';
import { raceSummary } from '../race/raceSummary';
import { placeLabel, type ResultRow, toResultRows } from '../results/resultRows';
import './ResultsScreen.css';

/** A gamepad's focus starts at «Ещё раз!». */
const padStart = { [PAD_START_ATTR]: '' };

/** Podium for the top three plus the full table. */
export function ResultsScreen() {
  const app = useApp();
  const results = app.race.results.value;
  const myId = app.session.playerId.value;
  const online = app.connection.status.value === 'online';
  if (!results) return null;

  const { settings, levelCount } = results;
  const timed = settings.format === 'time';
  const rows = toResultRows(results.results, myId, { format: settings.format, levelCount });
  const podium = rows.filter((row) => row.place !== null && row.place <= 3);

  return (
    <main class="screen results">
      <header class="results__head">
        <h1 class="results__title title-text">
          <span aria-hidden="true">🏆</span> {ru.results.title}
        </h1>
        {/* Which race it was: by levels or against the clock, how long, how hard. */}
        <p class="results__race">{raceSummary(settings)}</p>
      </header>
      <div class="results__layout">
        {podium.length > 0 && <Podium rows={podium} timed={timed} />}
        <div class="results__table-wrap panel">
          <table class="results__table">
            <thead>
              <tr>
                <th scope="col" class="results__col-place">
                  <span class="results__place-heading">{ru.results.place}</span>
                </th>
                <th scope="col" class="results__col-player">
                  {ru.results.player}
                </th>
                <th scope="col">{ru.results.time}</th>
                <th scope="col">
                  <span aria-hidden="true">🏁</span>
                  <span class="visually-hidden">{ru.results.levels}</span>
                </th>
                <th scope="col">
                  <span aria-hidden="true">💀</span>
                  <span class="visually-hidden">{ru.results.deaths}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} class={row.isMe ? 'results__me' : undefined}>
                  <td class="results__col-place">{placeLabel(row.place)}</td>
                  <td class="results__col-player">
                    <span class="results__player">
                      <Avatar
                        avatar={row.profile.avatar}
                        size="small"
                        ring={cubeAccent(row.profile.skin)}
                      />
                      <span class="results__name">{row.profile.name}</span>
                    </span>
                  </td>
                  {/* Without a finish the cell says why (a dash, «не в гонке»): no gold for it. */}
                  <td class={`results__time${row.place === null ? ' results__time--none' : ''}`}>
                    {row.time}
                  </td>
                  <td>{row.levels}</td>
                  <td>{row.deaths}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <footer class="results__footer" {...padStart}>
        <Button
          variant="green"
          size="big"
          icon="🔁"
          disabled={!online}
          onClick={() => app.playAgain()}
        >
          {ru.results.again}
        </Button>
        <Button variant="blue" size="big" onClick={() => app.closeResults()}>
          {ru.results.toRoom}
        </Button>
      </footer>
    </main>
  );
}

/**
 * Second, first and third place side by side, the winner on the highest step. Against the
 * clock the levels are the score, the time below them breaks ties.
 */
function Podium({ rows, timed }: { rows: readonly ResultRow[]; timed: boolean }) {
  const byPlace = (place: number) => rows.find((row) => row.place === place);
  const steps = [byPlace(2), byPlace(1), byPlace(3)];
  return (
    <ol class="podium">
      {steps.map((row) =>
        row ? (
          <li key={row.id} class={`podium__step podium__step--${row.place}`}>
            <Avatar avatar={row.profile.avatar} ring={cubeAccent(row.profile.skin)} />
            <CubePreview
              look={row.profile.skin}
              size={row.place === 1 ? 40 : 32}
              glow={row.place === 1}
              class="podium__cube"
            />
            <span class="podium__name">{row.profile.name}</span>
            {timed && <span class="podium__score">{ru.results.levelsDone(row.levelsDone)}</span>}
            <span class="podium__time">{row.time}</span>
            <span class="podium__block">{row.place}</span>
          </li>
        ) : null,
      )}
    </ol>
  );
}
